import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  LayoutDashboard,
  Wallet,
  CalendarClock,
  Users,
  GraduationCap,
  Megaphone,
  UserSquare2,
  Settings,
  LogOut,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { t } from '@/locales/i18n';
import { usePermissions, type ModuleKey } from '@/hooks/usePermissions';
import { signOut } from '@/lib/auth';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PaletteItem {
  module: ModuleKey | null;
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const ITEMS: PaletteItem[] = [
  { module: 'dashboard', to: '/', label: t('nav.dashboard'), icon: LayoutDashboard },
  { module: 'finances', to: '/finances', label: t('nav.finances'), icon: Wallet },
  { module: 'echeances', to: '/echeances', label: t('nav.echeances'), icon: CalendarClock },
  { module: 'crm', to: '/crm', label: t('nav.crm'), icon: Users },
  { module: 'formation', to: '/formation', label: t('nav.formation'), icon: GraduationCap },
  { module: 'marketing', to: '/marketing', label: t('nav.marketing'), icon: Megaphone },
  { module: 'rh', to: '/rh', label: t('nav.rh'), icon: UserSquare2 },
  { module: 'parametres', to: '/parametres', label: t('nav.parametres'), icon: Settings },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const visible = ITEMS.filter((item) => (item.module ? canAccess(item.module) : true));

  const go = (to: string): void => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0">
        <Command label="Command palette" className="flex flex-col">
          <Command.Input
            placeholder={t('common.search')}
            className="h-12 w-full border-b border-border bg-transparent px-4 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Command.List className="scrollbar-thin max-h-80 overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              {t('common.noResults')}
            </Command.Empty>
            <Command.Group heading={t('nav.sectionPilotage')}>
              {visible.map((item) => (
                <Command.Item
                  key={item.to}
                  value={item.label}
                  onSelect={() => go(item.to)}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm aria-selected:bg-secondary"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </Command.Item>
              ))}
            </Command.Group>
            <Command.Separator className="my-1 h-px bg-border" />
            <Command.Group heading="Compte">
              <Command.Item
                value="Se déconnecter"
                onSelect={async () => {
                  onOpenChange(false);
                  await signOut();
                  navigate('/login');
                }}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-destructive aria-selected:bg-secondary"
              >
                <LogOut className="h-4 w-4" />
                {t('nav.logout')}
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
